import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { portalLogout } from '../../redux/slices/portalAuthSlice';

const SIDEBAR_WIDTH = 240;
const ACCENT = '#0d4f4f';

function getInitials(user) {
  if (!user) return '?';
  const name = (user.fullName || '').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const email = (user.email || '').trim();
  if (email.length >= 2) {
    return email.slice(0, 2).toUpperCase();
  }
  return '?';
}

const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.portalAuth);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(portalLogout());
    navigate('/customer-complaint/login');
  };

  const navItems = [
    { to: '/customer-complaint/new-ticket', label: 'New ticket', icon: <AddIcon fontSize="small" /> },
    { to: '/customer-complaint/dashboard', label: 'My Tickets', icon: <ConfirmationNumberOutlinedIcon fontSize="small" /> },
    { to: '/customer-complaint/knowledge-base', label: 'Knowledge base', icon: <MenuBookOutlinedIcon fontSize="small" /> },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: '#ececec',
          borderRight: '1px solid #ddd',
          display: 'flex',
          flexDirection: 'column',
          py: 2,
          px: 1.5,
        }}
      >
        <Box
          component={RouterLink}
          to="/customer-complaint/dashboard"
          sx={{
            display: 'block',
            textAlign: 'center',
            mb: 2,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}/icon.png`}
            alt="Midtronix"
            sx={{ height: 36, maxWidth: 160, objectFit: 'contain' }}
          />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            letterSpacing={0.5}
            sx={{ mt: 0.5, color: '#333' }}
          >
            MIDTRONIX
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <List dense disablePadding sx={{ flex: 1 }}>
          {navItems.map((item) => {
            const selected = location.pathname === item.to;
            return (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                selected={selected}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0,0,0,0.06)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: selected ? ACCENT : 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: selected ? 600 : 400,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 2.5,
            py: 1.5,
            bgcolor: '#fff',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Avatar
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              bgcolor: ACCENT,
              width: 40,
              height: 40,
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            aria-label="Account menu"
          >
            {getInitials(user)}
          </Avatar>
          <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem disabled dense sx={{ opacity: 1 }}>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                {user?.fullName || user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default PortalLayout;
